import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { MeetingServiceOrder } from "../accountingTypes"
import { toCurrency, toDinero } from "../../../../currency/currencyLib"
import { MonetaryAmount } from "../../../../currency/currencyTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { useMoney } from "../../../../currency/useMoney"
import { OrderLine } from "../../../controlKitchenTypes"
import { OrganizerType } from "../../../../../shared/types/OrganizerTypes"

type useExportOrdersToCsvParams = Readonly<{
    data: MeetingServiceOrder[] | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportOrdersToCsv({ data, startDate, endDate }: useExportOrdersToCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-Orders-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })

    function getCurrencyString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const currency = toCurrency(amount.currency)
        return moneyFactory.getLocalizedCurrencySymbol(currency)
    }

    function getAmountString(amount: MonetaryAmount | undefined) {
        if (!amount) return ""
        const dinero = toDinero(amount)
        return moneyFactory.formatAsNumber(dinero)
    }

    const mapOrderToCsvRow = (order: MeetingServiceOrder, orderLine: OrderLine) => {
        return {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(endDate),
            "Order Id": order?.id?.toString() ?? "",
            "Payment method": l10n.getStringForEnum(
                "purchases-payment-method",
                order?.paymentDetails?.paymentMethod ?? "",
                "",
            ),
            Type: order?.paymentDetails?.type ?? "",
            "Booked date": order?.accountingDetails?.bookedTime
                ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.accountingDetails.bookedTime))
                : "",
            "Delivery date": order?.deliveryDetails?.time
                ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.deliveryDetails.time))
                : "",
            "Product id": orderLine?.productId?.toString() ?? "",
            "Product name": orderLine?.name ?? "",
            Items: orderLine?.items?.toString() ?? "",
            ["Item price " + `(${getCurrencyString(orderLine?.itemPrice)})`]: getAmountString(orderLine?.itemPrice),
            ["Amount " + `(${getCurrencyString(orderLine?.price)})`]: getAmountString(orderLine?.price),
            "Order note": order?.orderNote ?? "",
            "Delivery type": order?.deliveryDetails?.type ?? "",
            "Delivery location": order?.deliveryDetails?.location?.name ?? "",
            Sittings: order?.deliveryDetails?.sittings ?? "",
            "Meeting name": order?.meetingDetails?.meetingName ?? "",
            "Meeting host": order?.meetingDetails?.organizers
                ? order.meetingDetails.organizers
                      .filter((organizer) => organizer.type === OrganizerType.RESPONSIBLE)
                      .map((organizer) => organizer.name)
                      .join(", ")
                : "-",
            "Meeting start time": order?.meetingDetails?.startDateTime
                ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.meetingDetails.startDateTime))
                : "",
            "Meeting end time": order?.meetingDetails?.endDateTime
                ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.meetingDetails.endDateTime))
                : "",
            "Creator name": order?.customer?.displayName ?? "",
            "Creator email": order?.customer?.email ?? "",
            "Creator user group": order?.customer?.userGroup?.name ?? "",
            Company: order?.accountingCustomerParty?.name ?? "",
            "Sales Case Number": order?.sellerSupplierParty?.caseNumber ?? "",
            "Sales location": order?.sellerSupplierParty?.name ?? "",
            Channel: order?.paymentDetails?.salesChannel ?? "",
            "Debtor ID": order?.buyerCustomerParty?.debitorNo ?? "",
            "Debtor name": order?.buyerCustomerParty?.name ?? "",
            "Debtor address": order?.buyerCustomerParty?.address?.displayName ?? "",
            "Debtor contact": order?.buyerCustomerParty?.contactPerson ?? "",
            "Debtor contact email": order?.buyerCustomerParty?.email ?? "",
            "Debtor phone": order?.buyerCustomerParty?.phoneNumber ?? "",
            "Debtor EAN": order?.buyerCustomerParty?.EAN ?? "",
            "Cost center": order?.accountingDetails?.accountingCostCode ?? "",
            // TODO
            // "Debtor invoice email": '',
            // "Account Number": "",
            // "Participants": order.meetingDetails.attendees
        }
    }

    return useCallback(() => {
        const exportFallback = Object.keys(mapOrderToCsvRow({} as MeetingServiceOrder, {} as OrderLine)).reduce(
            (acc, key) => ({ ...acc, [key]: "" }),
            {},
        )
        const exportRows =
            data && data.length > 0
                ? data.flatMap((order) => {
                      const flatDeliveries = order.deliveries?.flatMap((delivery) => delivery.orderLines)
                      return flatDeliveries
                          ? flatDeliveries.map((orderLine) => mapOrderToCsvRow(order, orderLine))
                          : [exportFallback]
                  })
                : [exportFallback]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n])
}
